import React, { useState } from "react";
import { Plus, RefreshCw, Search, X } from "lucide-react";
import { DataType, Currency, Metal, EntryStatus } from "../../../types";
import { MultiSelect } from "../../../components/ui/MultiSelect";
import { DateRangePicker } from "../../../components/ui/DateRangePicker";

const TYPES: DataType[] = ["Trade Buy", "Trade Sell", "Swap - II gamba"];
const CURRENCIES: Currency[] = ["Euro", "Dollari"];
const METALS: Metal[] = ["Oro", "Argento", "Platino", "Palladio", "Iridio"];
const STATUSES: EntryStatus[] = ["Da processare", "Registra", "Confermato", "Ignora"];

type FilterKey = "types" | "currencies" | "opCurrencies" | "metals" | "statuses";

export function ToolbarFilters({
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  types,
  setTypes,
  currencies,
  setCurrencies,
  opCurrencies,
  setOpCurrencies,
  metals,
  setMetals,
  statuses,
  setStatuses,
  clearAll,
  addManualEntry,
  onGenerateEntries,
  generating,
  onSyncConfirmed,
  syncing,
  searchQuery,
  setSearchQuery,
}: {
  dateFrom: Date | null;
  setDateFrom: (v: Date | null) => void;
  dateTo: Date | null;
  setDateTo: (v: Date | null) => void;
  types: DataType[];
  setTypes: (v: DataType[]) => void;
  currencies: Currency[];
  setCurrencies: (v: Currency[]) => void;
  opCurrencies: Currency[];
  setOpCurrencies: (v: Currency[]) => void;
  metals: Metal[];
  setMetals: (v: Metal[]) => void;
  statuses: EntryStatus[];
  setStatuses: (v: EntryStatus[]) => void;
  clearAll: () => void;
  addManualEntry: () => void;
  onGenerateEntries: () => void;
  generating: boolean;
  onSyncConfirmed?: () => void;
  syncing?: boolean;
  searchQuery: string;
  setSearchQuery: (v: string) => void;
}) {
  const [openFilter, setOpenFilter] = useState<FilterKey | null>(null);

  const handleFilterOpen = (filter: FilterKey, isOpening: boolean) => {
    if (isOpening) {
      setOpenFilter(filter);
    } else if (openFilter === filter) {
      setOpenFilter(null);
    }
  };

  // Chiudi altri filtri quando se ne apre uno
  const handleFilterClick = (filter: FilterKey) => (isOpen: boolean) => {
    if (isOpen) {
      // Chiudi tutti gli altri filtri
      setOpenFilter(openFilter === filter ? null : filter);
    } else {
      handleFilterOpen(filter, isOpen);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3">

      {/* Ricerca fuzzy per nota e ID */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
        <input
          type="text"
          placeholder="Cerca nota o ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="rounded-xl border border-gray-200 pl-8 pr-7 py-2 text-sm shadow-sm w-48 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-300"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

    <DateRangePicker
      dateRange={{ start: dateFrom ?? null, end: dateTo ?? null }}
      onDateRangeChange={(range) => {
        setDateFrom(range.start);
        setDateTo(range.end);
      }}
    />

      {/* <div className="flex items-center gap-2">
        <input
          type="date"
          value={dateFrom}
          onChange={handleDateChange(setDateFrom)}
          className="rounded-xl border border-gray-200 px-3 py-2 text-sm shadow-sm"
        />
        <span className="text-sm text-gray-500">→</span>
        <input
          type="date"
          value={dateTo}
          onChange={handleDateChange(setDateTo)}
          className="rounded-xl border border-gray-200 px-3 py-2 text-sm shadow-sm"
        />
      </div> */}

      <MultiSelect
        label="Tipo di operazione"
        options={TYPES}
        values={types}
        setValues={setTypes}
        onOpenChange={handleFilterClick("types")}
      />
      <MultiSelect
        label="Valuta"
        options={CURRENCIES}
        values={currencies}
        setValues={setCurrencies}
        onOpenChange={handleFilterClick("currencies")}
      />
      <MultiSelect
        label="Val. operazione"
        options={CURRENCIES}
        values={opCurrencies}
        setValues={setOpCurrencies}
        onOpenChange={handleFilterClick("opCurrencies")}
      />
      <MultiSelect
        label="Metallo"
        options={METALS}
        values={metals}
        setValues={setMetals}
        onOpenChange={handleFilterClick("metals")}
      />
      <MultiSelect
        label="Stato"
        options={STATUSES}
        values={statuses}
        setValues={setStatuses}
        onOpenChange={handleFilterClick("statuses")}
      />

      <div className="ml-auto flex items-center gap-2">
        {/* <button
          type="button"
          onClick={clearAll}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm hover:bg-gray-50"
        >
          <Trash2 className="h-4 w-4" />
        </button> */}
        
        {/* <button
          type="button"
          onClick={onGenerateEntries}
          disabled={generating}
          className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm shadow-sm ${
            generating
              ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
              : "border-green-200 bg-green-50 text-green-700 hover:bg-green-100"
          }`}
        >
          {generating ? (
            <>
              <svg
                className="animate-spin h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Elaborazione...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" />
            </>
          )}
        </button> */}

        {onSyncConfirmed && (
          <button
            type="button"
            onClick={onSyncConfirmed}
            disabled={syncing}
            title="Sincronizza conferme dal gestionale"
            className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm shadow-sm ${
              syncing
                ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                : "border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
            }`}
          >
            <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Sincronizza</span>
          </button>
        )}

        <button
          type="button"
          onClick={addManualEntry}
          className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700 shadow-sm hover:bg-blue-100"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
